import React, { useState } from 'react';
import Icon from '../../../components/AppIcon';
import Button from '../../../components/ui/Button';
import Select from '../../../components/ui/Select';
import { t } from '../../../utils/translations';

const BulkActionsBar = ({ 
  selectedCount, 
  onBulkAction, 
  onClearSelection 
}) => {
  const [selectedAction, setSelectedAction] = useState('');
  const [isProcessing, setIsProcessing] = useState(false);

  const bulkActionOptions = [
    { value: '', label: t('articles.chooseAction') },
    { value: 'publish', label: t('articles.publishArticles') },
    { value: 'unpublish', label: t('articles.unpublishArticles') },
    { value: 'draft', label: t('articles.moveToDraft') },
    { value: 'delete', label: t('articles.deleteArticles') },
    { value: 'export', label: t('articles.exportArticles') },
    { value: 'duplicate', label: t('articles.duplicateArticles') }
  ];

  const handleActionExecute = async () => {
    if (!selectedAction) return;

    setIsProcessing(true);
    
    try {
      await onBulkAction(selectedAction);
      setSelectedAction('');
    } catch (error) {
      console.error('Bulk action failed:', error);
    } finally {
      setIsProcessing(false);
    }
  };

  const getActionIcon = (action) => {
    const iconMap = {
      publish: 'Eye',
      unpublish: 'EyeOff',
      draft: 'Edit3',
      delete: 'Trash2',
      export: 'Download',
      duplicate: 'Copy'
    };
    return iconMap?.[action] || 'Settings';
  };

  const getActionColor = (action) => {
    const colorMap = {
      publish: 'success',
      unpublish: 'warning',
      draft: 'secondary',
      delete: 'destructive',
      export: 'outline',
      duplicate: 'outline'
    };
    return colorMap?.[action] || 'default';
  };

  if (selectedCount === 0) return null;

  return (
    <div className="fixed bottom-6 left-1/2 transform -translate-x-1/2 z-300 animate-slide-up">
      <div className="bg-card border border-border rounded-lg shadow-elevation-3 p-4 min-w-96">
        <div className="flex items-center justify-between space-x-4 space-x-reverse">
          {/* Selection Info */}
          <div className="flex items-center space-x-3 space-x-reverse">
            <div className="w-8 h-8 bg-primary rounded-full flex items-center justify-center">
              <Icon name="CheckCircle" size={16} color="white" />
            </div>
            <div>
              <p className="text-sm font-medium text-foreground">
                {selectedCount} {selectedCount === 1 ? t('articles.articleSelected') : t('articles.articlesSelectedPlural')}
              </p>
              <p className="text-xs text-muted-foreground">
                {t('articles.chooseActionToApply')}
              </p>
            </div>
          </div>

          {/* Actions */}
          <div className="flex items-center space-x-2 space-x-reverse">
            <Select
              options={bulkActionOptions}
              value={selectedAction}
              onChange={setSelectedAction}
              placeholder={t('articles.chooseAction')}
              className="w-48"
              dropdownDirection="up"
            />

            <Button
              variant={selectedAction ? getActionColor(selectedAction) : 'outline'}
              size="sm"
              onClick={handleActionExecute}
              disabled={!selectedAction || isProcessing}
              loading={isProcessing}
              iconName={selectedAction ? getActionIcon(selectedAction) : 'Play'}
              iconPosition="left"
              iconSize={16}
            >
              {isProcessing ? t('common.processing') : t('common.apply')}
            </Button>

            <Button
              variant="ghost"
              size="sm"
              onClick={onClearSelection}
              iconName="X"
              iconSize={16}
              title={t('common.clear')}
            />
          </div>
        </div>

        {/* Action Preview */}
        {selectedAction && (
          <div className="mt-3 pt-3 border-t border-border">
            <div className="flex items-center space-x-2 space-x-reverse text-xs text-muted-foreground">
              <Icon name="Info" size={14} />
              <span>
                {selectedAction === 'delete' && t('articles.deleteWarning')}
                {selectedAction === 'publish' && t('articles.publishInfo')}
                {selectedAction === 'unpublish' && t('articles.unpublishInfo')}
                {selectedAction === 'draft' && t('articles.draftInfo')}
                {selectedAction === 'export' && t('articles.exportInfo')}
                {selectedAction === 'duplicate' && t('articles.duplicateInfo')}
              </span>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default BulkActionsBar;